import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, take, timer } from 'rxjs';
import { Router } from '@angular/router';

type SideSheetMode = 'desktop' | 'mobile';
type SideSheetPrefs = Record<string, Partial<Record<SideSheetMode, boolean>>>;

export const IS_DESKTOP = [Breakpoints.XLarge, Breakpoints.Large];
export const IS_TABLET = [Breakpoints.Medium];
export const IS_HANDSET = [Breakpoints.Small, Breakpoints.XSmall];

@Injectable({ providedIn: 'root' })
export class AppShellUIService {
  private _showSideSheet$ = new BehaviorSubject(true);
  private _showFooter$ = new BehaviorSubject(true);
  private _showHeader$ = new BehaviorSubject(true);
  private _showNavigationRail$ = new BehaviorSubject(true);
  private _showNavigationDrawer$ = new BehaviorSubject(true);
  private _showPageHeader$ = new BehaviorSubject(true);
  private _showPageFooter$ = new BehaviorSubject(true);
  private _highlightSideSheet$ = new BehaviorSubject(false);

  private _hadDrawer: boolean;
  private _hadRail: boolean;

  public openMenu$ = new BehaviorSubject(false);

  private _observer = inject(BreakpointObserver);
  private _router = inject(Router);

  private readonly SIDE_SHEET_PREFS_KEY = 'appShell.sideSheetPrefs';

  public visibleItemsConfig$ = combineLatest([
    this._showFooter$,
    this._showHeader$,
    this._showSideSheet$,
    this._showPageFooter$,
    this._showPageHeader$,
    this._showNavigationRail$,
    this._showNavigationDrawer$,
  ]).pipe(
    map(
      ([
        showFooter,
        showHeader,
        showSideSheet,
        showPageFooter,
        showPageHeader,
        showNavigationRail,
        showNavigationDrawer,
      ]) => ({
        showFooter,
        showHeader,
        showSideSheet,
        showPageFooter,
        showPageHeader,
        showNavigationRail,
        showNavigationDrawer,
      })
    )
  );

  public openMenu(primary?: boolean) {
    if (primary) {
      this._hadDrawer = this._showNavigationDrawer$.value;
      this._hadRail = this._showNavigationRail$.value;
      this._showNavigationRail$.next(true);
      this._showNavigationDrawer$.next(false);
    }

    this.openMenu$.next(true);
  }
  public closeMenu() {
    if (this._hadDrawer !== undefined && this._hadRail !== undefined) {
      this._showNavigationRail$.next(this._hadRail);
      this._showNavigationDrawer$.next(this._hadDrawer);
    }
    this._hadDrawer = undefined;
    this._hadRail = undefined;
    this.openMenu$.next(false);
  }

  public highlightItemsConfig$ = combineLatest([this._highlightSideSheet$]).pipe(
    map(([highlightSideSheet]) => ({ highlightSideSheet }))
  );

  public setShowNavigationRail(value: boolean) {
    this._showNavigationRail$.next(value);
  }

  public setShowSideSheet(value: boolean) {
    this._showSideSheet$.next(value);
  }

  public setShowFooter(value: boolean) {
    this._showFooter$.next(value);
  }

  public setShowHeader(value: boolean) {
    this._showHeader$.next(value);
  }

  public setShowNavigationDrawer(value: boolean) {
    this._showNavigationDrawer$.next(value);
  }

  public setShowPageHeader(value: boolean) {
    this._showPageHeader$.next(value);
  }

  public setShowPageFooter(value: boolean) {
    this._showPageFooter$.next(value);
  }

  /**
   * Pass `persist` for a toggle the user operated: the shell reapplies the stored preference on
   * every navigation, so a choice it never heard about is undone by the next one.
   */
  public toggleShowSideSheet(persist: boolean = false) {
    if (this._showSideSheet$.value) {
      this.closeSideSheet(persist);
    } else {
      this.openSideSheet(persist);
    }
  }

  public toggleShowFooter() {
    this._showFooter$.next(!this._showFooter$.value);
  }

  public toggleShowHeader() {
    this._showHeader$.next(!this._showHeader$.value);
  }

  public toggleShowNavigationDrawer() {
    this._showNavigationDrawer$.next(!this._showNavigationDrawer$.value);
  }

  public toggleShowPageHeader() {
    this._showPageHeader$.next(!this._showPageHeader$.value);
  }

  public toggleShowPageFooter() {
    this._showPageFooter$.next(!this._showPageFooter$.value);
  }

  /**
   * The block of `dcr-app-shell` that holds the page-header, the main content and the page-footer.
   * It lives in the shell's shadow root and is re-rendered with it, so resolve it when it is needed.
   */
  public getPageContentElement(): HTMLElement | null {
    return document.querySelector('dcr-app-shell')?.shadowRoot?.querySelector<HTMLElement>('.page-content') ?? null;
  }

  public highlightSideSheet() {
    this._highlightSideSheet$.next(true);
    timer(600)
      .pipe(take(1))
      .subscribe(() => this._highlightSideSheet$.next(false));
  }

  public get isHandset$(): Observable<boolean> {
    return this._observer.observe(IS_HANDSET).pipe(
      map((result) => result.matches),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  public get isTablet$(): Observable<boolean> {
    return this._observer.observe(IS_TABLET).pipe(
      map((result) => result.matches),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  public get isDesktop$(): Observable<boolean> {
    return this._observer.observe(IS_DESKTOP).pipe(
      map((result) => result.matches),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  public closeSideSheet(persist: boolean = false): void {
    this._showSideSheet$.next(false);

    if (persist) {
      const primaryRoot = this._getCurrentPrimaryRootSegment();
      const mode: SideSheetMode = this._observer.isMatched(IS_DESKTOP) ? 'desktop' : 'mobile';
      this.setSideSheetPreference(primaryRoot, mode, false);
    }
  }

  public openSideSheet(persist: boolean = false): void {
    this._showSideSheet$.next(true);

    if (persist) {
      const primaryRoot = this._getCurrentPrimaryRootSegment();
      const mode: SideSheetMode = this._observer.isMatched(IS_DESKTOP) ? 'desktop' : 'mobile';
      this.setSideSheetPreference(primaryRoot, mode, true);
    }
  }

  // Preference helpers

  public getSideSheetPreference(primaryRoot: string, mode: SideSheetMode): boolean | null {
    if (!primaryRoot) return null;
    const prefs = this._readSideSheetPrefs();
    const rootPrefs = prefs[primaryRoot];
    if (!rootPrefs || typeof rootPrefs[mode] !== 'boolean') return null;
    return rootPrefs[mode] as boolean;
  }

  public setSideSheetPreference(primaryRoot: string, mode: SideSheetMode, value: boolean): void {
    if (!primaryRoot) return;
    const prefs = this._readSideSheetPrefs();
    prefs[primaryRoot] = prefs[primaryRoot] || {};
    prefs[primaryRoot][mode] = value;
    this._writeSideSheetPrefs(prefs);
  }

  public clearSideSheetPreference(primaryRoot?: string, mode?: SideSheetMode): void {
    if (!primaryRoot) {
      localStorage.removeItem(this.SIDE_SHEET_PREFS_KEY);
      return;
    }
    const prefs = this._readSideSheetPrefs();
    if (!prefs[primaryRoot]) return;
    if (!mode) {
      delete prefs[primaryRoot];
    } else {
      delete prefs[primaryRoot][mode];
      if (Object.keys(prefs[primaryRoot]).length === 0) delete prefs[primaryRoot];
    }
    this._writeSideSheetPrefs(prefs);
  }

  private _readSideSheetPrefs(): SideSheetPrefs {
    try {
      const raw = localStorage.getItem(this.SIDE_SHEET_PREFS_KEY);
      return raw ? (JSON.parse(raw) as SideSheetPrefs) : {};
    } catch {
      return {};
    }
  }

  private _writeSideSheetPrefs(prefs: SideSheetPrefs): void {
    try {
      localStorage.setItem(this.SIDE_SHEET_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // ignore storage errors
    }
  }

  // Utility: derive current primary root from router url.
  private _getCurrentPrimaryRootSegment(): string {
    try {
      const tree = this._router.parseUrl(this._router.url);
      const primary = tree.root.children['primary'];
      if (!primary || primary.segments.length === 0) return '';
      return primary.segments[0]?.path || '';
    } catch {
      return '';
    }
  }
}
